import { Loader2 } from "lucide-react";
import { Link, useParams } from "react-router";

import { Eyebrow } from "@/components/eyebrow";
import { MarkdownArticle } from "@/components/markdown-article";
import { PageShell } from "@/components/page-shell";
import { Scorecard } from "@/components/scorecard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { type AssessmentInternalRecord, useAssessmentInternal } from "@/lib/api";

import { CalibrationForm } from "./calibration-form";

export function ExpertReviewPage() {
  const { id } = useParams<{ id: string }>();
  const query = useAssessmentInternal(id ?? null);

  const status = (query.error as { status?: number } | null)?.status;

  return (
    <PageShell>
      {query.isLoading && (
        <div className="flex justify-center py-24">
          <Loader2 className="text-muted h-6 w-6 animate-spin" />
        </div>
      )}
      {query.error != null &&
        (status === 404 ? (
          <div className="py-12 text-center">
            <Eyebrow>404</Eyebrow>
            <h1 className="text-primary mt-3 text-4xl font-normal tracking-tight sm:text-5xl">
              Not found.
            </h1>
            <p className="text-muted-foreground mt-4">That assessment or page doesn’t exist.</p>
            <Button asChild variant="outline" className="mt-8">
              <Link to="/expert">Back to the queue</Link>
            </Button>
          </div>
        ) : (
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground text-sm">
                Couldn’t load the assessment. Please try again.
              </p>
              <Button variant="outline" className="mt-4" onClick={() => void query.refetch()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        ))}
      {query.data && <ExpertReviewBody record={query.data} />}
    </PageShell>
  );
}

interface ExpertReviewBodyProps {
  record: AssessmentInternalRecord;
}

function ExpertReviewBody({ record }: ExpertReviewBodyProps) {
  const { result, assessment, owner_name, owner_email } = record;

  return (
    <div>
      <p className="mb-3 flex gap-4 font-mono text-xs">
        <Link to="/expert" className="text-accent hover:underline">
          ← Review queue
        </Link>
        <Link to={`/report/${record.assessment_id}`} className="text-accent hover:underline">
          Client view
        </Link>
      </p>
      <Eyebrow>Expert review</Eyebrow>
      <h1 className="text-primary mt-3 text-4xl font-normal tracking-tight sm:text-5xl">
        {assessment.company_name}.
      </h1>
      <p className="text-muted mt-2">
        {assessment.sector} · {assessment.business_model_type || "-"} · respondent:{" "}
        {assessment.respondent_role || "-"}
      </p>
      {(owner_name || owner_email) && (
        <p className="text-muted mt-1 text-sm">
          Client: {owner_name && <span className="text-primary font-medium">{owner_name}</span>}
          {owner_name && owner_email && " · "}
          {owner_email && (
            <a href={`mailto:${owner_email}`} className="text-accent hover:underline">
              {owner_email}
            </a>
          )}
        </p>
      )}

      <Card className="mt-6">
        <CardContent className="pt-6">
          <Scorecard
            businessModel={result.business_model_disruption_score}
            operatingModel={result.operating_model_disruption_score}
            protector={result.structural_protector_score}
            overall={result.overall_exposure_score}
            confidence={result.confidence_level}
            exposureLevel={result.exposure_level}
            strategicPosition={result.strategic_position}
          />
          <p className="text-muted mt-4 text-xs">
            {record.calibration_count > 0
              ? "Calibrated scores from the latest expert review. The original system scores remain stored unchanged."
              : "Original system scores. Calibration below records an expert-adjusted view without overwriting them."}
          </p>
        </CardContent>
      </Card>

      {record.prefill_summary && (
        <Card className="bg-background mt-5 border-0">
          <CardContent className="pt-6">
            <Eyebrow>Prefill provenance</Eyebrow>
            <p className="mt-2 text-sm">{record.prefill_summary}</p>
          </CardContent>
        </Card>
      )}

      {assessment.free_text_context && (
        <Card className="mt-5">
          <CardContent className="pt-6">
            <h3 className="text-primary text-xl font-normal tracking-tight">Client context</h3>
            <p className="text-muted mt-2 text-sm whitespace-pre-wrap">
              {assessment.free_text_context}
            </p>
          </CardContent>
        </Card>
      )}

      <Card className="mt-5">
        <CardContent className="pt-6">
          <MarkdownArticle markdown={record.report_markdown} />
        </CardContent>
      </Card>

      <h2 className="text-primary mt-10 text-2xl font-normal tracking-tight sm:text-3xl">
        Expert calibration
      </h2>
      {record.calibration_count > 0 && (
        <div className="border-accent bg-accent-light/30 text-primary mt-4 rounded-md border px-4 py-3 text-sm">
          {record.calibration_count} calibration{record.calibration_count > 1 ? "s" : ""} recorded.
          The latest calibration is reflected in this internal expert view. The public client report
          remains the original system report. Submitting again adds another.
        </div>
      )}
      <CalibrationForm
        assessmentId={record.assessment_id}
        defaults={{
          businessModel: result.business_model_disruption_score,
          operatingModel: result.operating_model_disruption_score,
          protector: result.structural_protector_score,
          strategicPosition: result.strategic_position,
        }}
      />
    </div>
  );
}
